import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Loader2, ShieldCheck, Sun, Leaf, Eye, EyeOff } from "lucide-react";
import { APP_VERSION } from "@/version";

const REMEMBER_KEY = "etel_remember_email";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      if (remember) {
        localStorage.setItem(REMEMBER_KEY, email.trim());
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* LEFT: hero / brand panel */}
      <div
        className="relative hidden lg:flex flex-col justify-between p-10 text-white overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(11,42,74,0.72) 0%, rgba(14,120,194,0.55) 100%), url(https://images.pexels.com/photos/356036/pexels-photo-356036.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10">
          <Logo variant="light" size="lg" />
        </div>

        <div className="relative z-10 space-y-6">
          <h1 className="font-display font-extrabold text-5xl leading-[1.05] tracking-tight max-w-xl">
            La energía del sol,<br />
            <span className="bg-gradient-to-r from-[#FFC740] to-[#FF8A1F] bg-clip-text text-transparent">
              tu parte de trabajo digital.
            </span>
          </h1>
          <p className="text-white/85 text-base max-w-md leading-relaxed">
            Registra tus partes en obra desde el móvil, con dictado por voz e
            integración directa con tu ERP. Pensado para instaladores en Canarias.
          </p>

          <div className="grid grid-cols-3 gap-3 max-w-lg pt-2">
            {[
              { icon: Sun, label: "Fotovoltaica 360°" },
              { icon: ShieldCheck, label: "Datos seguros" },
              { icon: Leaf, label: "Eficiencia real" },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="rounded-xl bg-white/10 backdrop-blur-md border border-white/15 px-3 py-3 flex flex-col items-start gap-2"
              >
                <Icon size={20} className="text-[#FFC740]" />
                <span className="text-xs font-semibold leading-tight">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-white/70">
          © {new Date().getFullYear()} Suntelco · Energías Renovables · Lanzarote · Gran Canaria
        </div>

        {/* decorative sun */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full opacity-30"
          style={{ background: "radial-gradient(closest-side, #FFC740 0%, transparent 70%)" }}
        />
      </div>

      {/* RIGHT: form */}
      <div className="relative flex items-center justify-center p-6 lg:p-12 bg-[#F7FAFD]">
        {/* Version badge — bottom right */}
        <div
          data-testid="app-version-badge"
          className="absolute bottom-3 right-4 z-20 flex items-center gap-1.5 text-[10px] font-mono text-slate-400 select-none"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#F5A623] animate-pulse" />
          <span className="uppercase tracking-[0.2em]">beta</span>
          <span className="text-slate-300">·</span>
          <span>v{APP_VERSION}</span>
        </div>
        <div
          aria-hidden
          className="absolute inset-0 grid-pattern opacity-60 pointer-events-none"
        />
        <div className="relative z-10 w-full max-w-md">
          <div className="lg:hidden mb-8 flex flex-col items-center text-center gap-3">
            <Logo variant="dark" size="lg" />
            <p className="text-sm text-slate-500 max-w-xs">
              Sistema interno · Parte de trabajo digital para instaladores
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_30px_60px_-30px_rgba(11,42,74,0.25)] p-8">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#F5A623]">
                Bienvenido
              </span>
              <span className="h-px flex-1 bg-gradient-to-r from-[#F5A623]/60 to-transparent" />
            </div>
            <h2 className="font-display font-extrabold text-3xl text-[#0B2A4A] leading-tight">
              Inicia sesión
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              Accede para registrar el parte diario de tu equipo en obra.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form" autoComplete="on">
              <div>
                <label className="label-up block mb-1.5" htmlFor="login-email-input">Email</label>
                <input
                  id="login-email-input"
                  name="email"
                  data-testid="login-email"
                  className="brutal-input w-full"
                  type="email"
                  required
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="operario@suntelco.es"
                />
              </div>

              <div>
                <label className="label-up block mb-1.5" htmlFor="login-password-input">Contraseña</label>
                <div className="relative">
                  <input
                    id="login-password-input"
                    name="password"
                    data-testid="login-password"
                    className="brutal-input w-full pr-12"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    aria-pressed={showPassword}
                    className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-500 hover:text-[#FF4500] transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="login-remember"
                  className="h-4 w-4 rounded border-slate-300 text-[#F5A623] focus:ring-[#F5A623]"
                />
                Recordar mi email en este dispositivo
              </label>

              <button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="brutal-btn-primary w-full inline-flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                Entrar
              </button>
            </form>
          </div>

          <p className="mt-6 text-xs text-center text-slate-400">
            Plataforma desarrollada para Suntelco · Gestión de partes de trabajo
          </p>
        </div>
      </div>
    </div>
  );
}
